import { memo, useCallback, useMemo, useState } from 'react';

import * as Sentry from '@sentry/react';
import Fuse from 'fuse.js';
import gql from 'graphql-tag';
import { Search } from 'lucide-react';
import { z } from 'zod';

import { Input } from '@/shadcn/input';
import { cn } from '@/shadcn/lib/utils';

import { notify } from '@/components/toast';
import useInput from '@/utils/hooks/useInput';
import WorkflowRow, {
  fragments as workflowRowFragments
} from './row/row.workflowList';

const favoriteWorkflowsSchema = z.array(z.string());

// Isolated so its internal keystroke state never re-renders the list
const WorkflowSearchInput = memo(({ onDebouncedChange }) => {
  const [searchQuery, onSearchChange] = useInput('', onDebouncedChange, 200);

  return (
    <div className="relative flex items-center">
      <Search className="pointer-events-none absolute left-2.5 h-3.5 w-3.5 text-muted-foreground" />
      <Input
        autoFocus
        className="h-7 w-48 pl-8 text-sm"
        onChange={onSearchChange}
        placeholder="Search workflows…"
        type="search"
        value={searchQuery}
      />
    </div>
  );
});

export const WorkflowsList = memo(
  ({ className = '', workflows = {}, isPrivileged = false, userId }) => {
    const { nodes: workflowsList = [] } = workflows;
    const [favWfIds, setFavWfIds] = useState();
    const [debouncedQuery, setDebouncedQuery] = useState('');

    const favoriteStoreKey = `${userId}-favoriteWorkflows`;

    // stable ref so WorkflowSearchInput's memo is never broken
    const onDebouncedChange = useCallback(
      value => setDebouncedQuery(value),
      []
    );

    const onFavoriteChange = (rowId, value) => {
      const toggleRowIdInList = list => {
        const prevList = list ?? [];
        return value
          ? [...prevList, rowId]
          : prevList.filter(id => id !== rowId);
      };

      setFavWfIds(prev => toggleRowIdInList(prev));

      try {
        const favoriteWorkflows = getStoredFavoriteWorkflows();
        const updated = toggleRowIdInList(favoriteWorkflows);
        localStorage.setItem(favoriteStoreKey, JSON.stringify(updated));
      } catch (error) {
        console.error('Error updating favorite workflows:', error);
        Sentry.captureException(error, {
          tags: { component: 'WorkflowsList' },
          extra: { rowId, value }
        });
        notify.error(
          'An error occurred while updating favorite workflows. Please try again.'
        );
      }
    };

    const getStoredFavoriteWorkflows = () => {
      try {
        const rawData = JSON.parse(
          localStorage.getItem(favoriteStoreKey) || '[]'
        );
        return favoriteWorkflowsSchema.parse(rawData);
      } catch (error) {
        console.error(
          'Error parsing favorite workflows from localStorage:',
          error
        );
        Sentry.captureException(error, {
          tags: { component: 'WorkflowsList' },
          extra: { favoriteStoreKey }
        });
        return [];
      }
    };

    const getSortedWorkflows = () => {
      const favWorkflowIds = favWfIds || getStoredFavoriteWorkflows();
      if (!favWfIds) {
        setFavWfIds(favWorkflowIds);
      }
      const favoriteWorkflows = workflowsList.filter(workflow =>
        favWorkflowIds.includes(workflow.rowId)
      );
      const nonFavoriteWorkflows = workflowsList.filter(
        workflow => !favWorkflowIds.includes(workflow.rowId)
      );
      return [...favoriteWorkflows, ...nonFavoriteWorkflows];
    };

    const sortedWorkflows = useMemo(getSortedWorkflows, [
      favWfIds,
      workflowsList
    ]);

    const fuse = useMemo(
      () =>
        new Fuse(sortedWorkflows, {
          keys: ['name', 'description'],
          threshold: 0.4,
          includeScore: true
        }),
      [sortedWorkflows]
    );

    const filteredWorkflows = useMemo(
      () =>
        debouncedQuery.trim()
          ? fuse.search(debouncedQuery).map(r => r.item)
          : sortedWorkflows,
      [debouncedQuery, sortedWorkflows, fuse]
    );

    return (
      <div
        className={cn(
          className,
          'flex shrink-0 flex-col flex-nowrap overflow-visible bg-card px-4 py-8'
        )}
      >
        <div className="mb-4 flex flex-row items-center justify-between px-4">
          <div className="mr-8 flex shrink-0 flex-col flex-nowrap text-justify font-semibold text-base">
            Your workflows
            <div className="h-1 w-4 rounded-lg bg-secondary" />
          </div>
          <WorkflowSearchInput onDebouncedChange={onDebouncedChange} />
        </div>
        <div className="scrollbar-thin relative max-h-[238px] overflow-y-auto">
          {filteredWorkflows.length > 0 ? (
            <table className="w-full border-spacing-0">
              <thead className="sticky top-0 z-1 whitespace-nowrap bg-card font-medium text-muted-foreground text-xs tracking-wider">
                <tr className="bg-card [&>th]:px-4">
                  <th className="relative w-1/2 min-w-37 pt-4 pb-2.5 font-medium">
                    NAME
                  </th>
                  <th className="relative whitespace-nowrap pt-4 pb-2.5 font-medium">
                    TRACES
                  </th>
                  <th className="relative whitespace-nowrap pt-4 pb-2.5 font-medium">
                    GROUPS
                  </th>
                  <th className="relative pt-4 pb-2.5 font-medium">
                    WORKFLOW DOCUMENTATION
                  </th>
                  <th className="relative w-0 pt-4 pb-2.5 font-medium" />
                </tr>
              </thead>
              <tbody className="whitespace-nowrap text-card-foreground">
                {filteredWorkflows.map(w => {
                  const isFavorite = favWfIds?.includes(w.rowId);
                  return (
                    <WorkflowRow
                      isFavorite={isFavorite}
                      isPrivileged={isPrivileged}
                      key={w.rowId}
                      onFavoriteChange={onFavoriteChange}
                      userId={userId}
                      workflow={w}
                    />
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="flex h-[86px] w-full items-center justify-center">
              <div className="text-center text-card-foreground text-sm">
                {debouncedQuery.trim()
                  ? 'No matching workflow.'
                  : 'No workflow.'}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
);

// export the workflows table query
export const fragments = {
  workflows: gql`
    fragment WorkflowsFragment on Query {
      workflows(filter: { draft: { equalTo: false } }) {
        totalCount
        nodes {
          rowId
          ...WorkflowRowFragment
        }
      }
    }
    ${workflowRowFragments.workflowRow}
  `
};
